import React from 'react';
import { BarChart, Trash2, Layers, MapPin, Calendar, Users, CreditCard } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных поездок
 */
const ComparisonSidebar = ({ 
  savedTrips, 
  deleteSavedTrip, 
  loadSavedTrip,
  getDestinationName 
}) => {
  // Если нет сохраненных поездок
  if (!savedTrips || savedTrips.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-purple-500" />
          Сравнение поездок
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты поездок для сравнения.
        </p>
      </div>
    );
  }

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-purple-500" />
          Сравнение поездок
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedTrips.length} {pluralize(savedTrips.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      {/* Список сохраненных поездок */}
      <div className="space-y-4">
        {savedTrips.map((trip) => (
          <div 
            key={trip.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {trip.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedTrip(trip.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedTrip(trip.id)}
                  title="Удалить"
                />
              </div>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center mb-1">
                <MapPin className="w-3.5 h-3.5 text-gray-500 dark:text-gray-500 mr-1.5" />
                <span>
                  {getDestinationName(trip.destination)}
                </span>
              </div>

              <div className="flex items-center mb-1">
                <Calendar className="w-3.5 h-3.5 text-gray-500 dark:text-gray-500 mr-1.5" />
                <span>
                  {trip.formData.tripDuration} {pluralize(parseInt(trip.formData.tripDuration, 10), 'день', 'дня', 'дней')}
                </span>
              </div>

              <div className="flex items-center mb-1">
                <Users className="w-3.5 h-3.5 text-gray-500 dark:text-gray-500 mr-1.5" />
                <span>
                  {trip.formData.travelers} {pluralize(parseInt(trip.formData.travelers, 10), 'человек', 'человека', 'человек')}
                </span>
              </div>

              {trip.results && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between mb-1">
                    <span className="flex items-center">
                      <CreditCard className="w-3.5 h-3.5 text-purple-500 mr-1.5" />
                      Общая стоимость:
                    </span>
                    <span className="font-semibold text-purple-600 dark:text-purple-400">
                      {formatCurrency(trip.results.totalCost, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>На человека:</span>
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      {formatCurrency(trip.results.costPerPerson, 0)}
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                Сохранено: {formatDate(trip.date)}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Если много сохраненных поездок, добавим опцию сравнения */}
      {savedTrips.length >= 2 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              Выберите поездки для сравнения
            </span>
            <Button
              size="sm"
              variant="outline"
              icon={<BarChart className="w-4 h-4" />}
            >
              Сравнить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;